import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TRACKING_FORMAT = /^SE-\d{9}$/;

export default function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState("");
  const [formatError, setFormatError] = useState(false);

  const handleTrack = () => {
    const trimmed = trackingId.trim().toUpperCase();
    if (!trimmed) return;
    if (!TRACKING_FORMAT.test(trimmed)) {
      setFormatError(true);
      return;
    }
    setFormatError(false);
    sessionStorage.setItem("tracking_navigated", "1");
    navigate(`/tracking/${encodeURIComponent(trimmed)}`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTrackingId(e.target.value);
    if (formatError) setFormatError(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleTrack();
  };

  return (
    <section id="track-section" className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20global%20logistics%20network%2C%20cargo%20planes%2C%20shipping%20containers%2C%20world%20map%20connections%2C%20dark%20navy%20blue%20tones%2C%20professional%20photography%2C%20ultra%20wide%20angle%2C%20cinematic%20lighting%2C%20high%20detail&width=1440&height=700&seq=hero-bg-001&orientation=landscape"
          alt="Global logistics network"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/60 to-slate-900/80" />
      </div>

      {/* Dot grid overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center pt-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white/90 text-sm font-medium">Real-time Tracking Active</span>
        </div>

        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
          {t("home_hero_title")}
        </h1>
        <p className="text-white/70 text-lg md:text-xl mb-10">
          {t("home_hero_subtitle")}
        </p>

        {/* Search Bar */}
        <div className="flex flex-col w-full max-w-xl mx-auto gap-2">
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <input
              type="text"
              value={trackingId}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="SE-000000000"
              className={`w-full sm:flex-1 h-14 px-5 rounded-lg bg-white text-gray-800 text-base placeholder-gray-400 outline-none border-2 transition-colors ${
                formatError ? "border-red-400" : "border-transparent focus:border-emerald-400"
              }`}
            />
            <button
              onClick={handleTrack}
              className="w-full sm:w-auto h-14 px-8 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-base"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line" />
              </div>
              {t("home_search_btn")}
            </button>
          </div>
          {formatError && (
            <p className="text-red-300 text-sm text-left flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line" />
              </span>
              Invalid format. Tracking numbers must follow: SE-000000000 (SE- + 9 digits)
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
